/*eslint-disable */
import React, { useState } from 'react';
import { ChevronLeft, ExternalLink } from 'lucide-react';
import { createReceipt,  Receipt } from '../../services/api';

interface ReceiptConfirmationPageProps {
  onBack: () => void;
  onIssued: (issuedAt: string) => void;
  receiptData: {
    recipientName: string;
    memo: string;
    emailAddress: string;
  };
  userType: 'guest' | 'cast';
  userId: number;
  transactionData?: {
    amount: number;
    type: string;
    description?: string;
    created_at: string;
  };
}

const ReceiptConfirmationPage: React.FC<ReceiptConfirmationPageProps> = ({ 
  onBack, 
  onIssued,
  receiptData,
  userType,
  userId,
  transactionData 
}) => {
  const [receipt, setReceipt] = useState<Receipt | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [shareableUrl, setShareableUrl] = useState<string>('');

  const handleCreateReceipt = async () => {
    if (!transactionData) {
      setError('取引データが見つかりません');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Convert points to yen for guest receipts using 1 point = 12 yen
      // Ensure a positive base amount for receipt purposes
      const rawAmount = Math.abs(Number(transactionData.amount) || 0);
      const YEN_PER_POINT = 1.2;
      const amountForReceipt = userType === 'guest'
        ? Math.round(rawAmount * YEN_PER_POINT)
        : rawAmount;

      const newReceipt = await createReceipt({
        user_type: userType,
        user_id: userId,
        recipient_name: receiptData.recipientName,
        amount: amountForReceipt,
        purpose: receiptData.memo || 'Pishatto利用料',
        transaction_created_at: transactionData.created_at,
      });

      setReceipt(newReceipt);
      if (newReceipt.issued_at) {
        onIssued(newReceipt.issued_at);
      }
      
      // Generate shareable URL for the receipt
      const baseUrl = window.location.origin;
      const receiptUrl = `${baseUrl}/receipt/${newReceipt.receipt_number}-${Math.random().toString(36).slice(2,10)}_${Math.random().toString(36).slice(2,10)}`;
      setShareableUrl(receiptUrl);
    } catch (err) {
      setError('領収書の作成に失敗しました');
      console.error('Receipt creation failed:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-gradient-to-b from-primary via-primary to-secondary">
      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-gray-200 bg-primary">
        <button onClick={onBack} className="mr-2 text-white hover:text-secondary cursor-pointer">
          <ChevronLeft size={20} />
        </button>
        <span className="text-sm font-medium flex-1 text-center text-white">全受信</span>
      </div>

      {/* Reply-to Address */}
      <div className="px-4 py-2 bg-primary">
        <div className="text-sm text-white text-center">返信先: no-reply@Pishatto.today</div>
      </div>

      {/* Email Content */}
      <div className="px-4 py-6 space-y-4">
        {/* Subject */}
        <div className="font-bold text-lg text-white">
          【Pishatto】領収書を発行しました
        </div>

        {/* Recipient */}
        <div className="text-white">
          {receiptData.recipientName}様
        </div>

        {/* Greeting */}
        <div className="text-white leading-relaxed">
          先日はPishattoをご利用いただきありがとうござい<br />
          ました。
        </div>

        {/* Receipt Confirmation */}
        <div className="text-white">
          領収書が発行されました。
        </div>

        {/* Receipt Link */}
        <div className="py-2">
          {error && (
            <div className="text-red-500 text-sm mb-2">{error}</div>
          )}
          <button
            onClick={handleCreateReceipt}
            disabled={loading}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed mb-3"
          >
            {loading ? '領収書を作成中...' : '領収書を発行'}
          </button>
          
          {/* Share and Display Options */}
          {shareableUrl && (
            <div className="space-y-2">
              <div className="text-xs text-white opacity-80 mb-2">
                領収書のURLを共有するか、直接アクセスできます
              </div>
              

              {/* Open Receipt Button */}
              <a
                href={shareableUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
              >
                <ExternalLink size={16} />
                領収書を表示
              </a>
              
              {/* Direct URL Display */}
              <div className="bg-white bg-opacity-10 rounded-lg p-3">
                <div className="text-xs text-white opacity-80 mb-1">共有用URL:</div>
                <a href={shareableUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-200 underline break-all">{shareableUrl}</a>
              </div>
            </div>
          )}
        </div>

        {/* Closing */}
        <div className="text-white">
          またのご利用心よりお待ちしております。
        </div>
      </div>

      {/* Promotional Section */}
      <div className="px-4 py-6 border-t border-gray-200">
        <div className="text-center text-sm text-white mb-3">
          \\\ Pishattoの楽しみ方 ///
        </div>
        <div className="space-y-1 text-sm text-white">
          <div>面談済みキャストと確実に会える!</div>
          <div>使えば使うほど好みのキャストを最適マッチング!</div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptConfirmationPage; 